import MenuIcon from "@mui/icons-material/Menu";
import { Button, Checkbox, FormControlLabel, FormGroup, Popover } from "@mui/material";
import { Box, Stack, SxProps, Theme } from "@mui/system";
import { useDragReorder } from "hooks/useDragReorder";
import { forwardRef, ReactNode, useRef, useState } from "react";

// NS-731: shared "button + popover with a drag-reorderable checkbox list",
// extracted from the identical implementations in Activity's ColumnSelector
// and FilterSelector.

export interface CheckListItem {
    label: string
    checked: boolean
    disabled: boolean
}

export interface ReorderableCheckListProps {
    buttonLabel: string
    open: boolean
    onButtonClick: () => void
    onClose: () => void
    /** Rendered above the list, inside the popover. */
    header?: ReactNode
    /** Rendered below the list, inside the popover. */
    footer?: ReactNode
    items: CheckListItem[]
    onToggle: (index: number) => void
    /** Called live while dragging, each time the dragged item crosses a neighbour. */
    onReorder: (from: number, to: number) => void
}

interface DragState {
    label: string
    checked: boolean
    initialPos: { x: number, y: number, w: number, h: number }
    pivot: { x: number, y: number }
    destinationRow: number
}

export function ReorderableCheckList(props: ReorderableCheckListProps) {
    const [dragState, setDragState] = useState<DragState>();

    const buttonRef = useRef<HTMLButtonElement>(null);
    const listbodyRef = useRef<HTMLDivElement>(null);
    const dragElementRef = useRef<HTMLDivElement>(null);

    useDragReorder(dragState, {
        axis: "y",
        ghostRef: dragElementRef,
        pivot: s => s.pivot,
        index: s => s.destinationRow,
        ghostSize: s => ({ w: s.initialPos.w, h: s.initialPos.h }),
        getBoxes: () => listbodyRef.current ? (Array.from(listbodyRef.current.childNodes) as Element[]).map(x => x.getBoundingClientRect()) : undefined,
        onIndexChange: (s, next) => {
            props.onReorder(s.destinationRow, next);
            setDragState({ ...s, destinationRow: next });
        },
        onDrop: () => setDragState(undefined),
    });

    return <>
        <Button ref={buttonRef} variant="outlined" onClick={props.onButtonClick}>{props.buttonLabel}</Button>
        <Popover
            open={props.open}
            anchorEl={buttonRef.current}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            onClose={props.onClose}
        >
            <Box sx={{ padding: "10px" }}>
                {props.header}
                <FormGroup ref={listbodyRef}>
                    {props.items.map((x, idx) => {
                        return <CheckListRow
                            key={idx}
                            dragging={dragState?.destinationRow === idx}
                            label={x.label}
                            checked={x.checked}
                            disabled={x.disabled}
                            onChange={() => props.onToggle(idx)}
                            onDrag={(pageX, pageY) => {
                                if (dragState || !listbodyRef.current) {
                                    return;
                                }

                                const rowArea = (listbodyRef.current.childNodes[idx] as Element).getBoundingClientRect();
                                setDragState({
                                    checked: x.checked,
                                    label: x.label,
                                    destinationRow: idx,
                                    initialPos: {
                                        x: rowArea.left,
                                        y: rowArea.top,
                                        w: rowArea.width,
                                        h: rowArea.height,
                                    },
                                    pivot: {
                                        x: pageX - rowArea.left,
                                        y: pageY - rowArea.top,
                                    }
                                });
                            }}
                        />;
                    })}
                </FormGroup>
                {props.footer}
            </Box>
        </Popover>
        {dragState &&
            <CheckListRow
                sx={{
                    left: dragState.initialPos.x,
                    top: dragState.initialPos.y,
                    width: dragState.initialPos.w,
                    height: dragState.initialPos.h,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.05)",
                    position: "absolute",
                    zIndex: 9999999999,
                    backgroundColor: "background.paper"
                }}
                ref={dragElementRef}
                label={dragState.label}
                checked={dragState.checked}
                disabled={false}
                data-testid="reorderable-checklist-ghost"
            />
        }
    </>;
}

export interface CheckListRowProps {
    sx?: SxProps<Theme>
    dragging?: boolean
    checked: boolean
    disabled: boolean
    onChange?: () => void
    onDrag?: (x: number, y: number) => void
    label: string
    "data-testid"?: string
}

export const CheckListRow = forwardRef<HTMLDivElement, CheckListRowProps>((props, ref) => {
    return <Stack
        ref={ref}
        direction="row"
        alignItems="center"
        data-testid={props["data-testid"] ?? "checklist-row"}
        sx={{
            position: "relative",
            ...props.sx,
        }}
    >
        <Box data-testid="drag-handle" onMouseDown={(x) => { props.onDrag && props.onDrag(x.pageX, x.pageY); }}>
            <MenuIcon
                sx={{
                    transform: "scale(0.8)",
                    marginRight: "15px",
                    color: "custom.muted",
                    cursor: "pointer"
                }}
            />
        </Box>
        <FormControlLabel control={
            <Checkbox
                checked={props.checked}
                disabled={props.disabled}
                onChange={props.onChange}
            />
        } label={props.label} />
        {props.dragging && <Box sx={{ position: "absolute", backgroundColor: "custom.highlight", left: "0", top: "0", width: "100%", height: "100%", zIndex: "9999999" }} />}
    </Stack>;
});
